<script setup>
import { useTeletelFeatures } from 'dashboard/composables/useTeletelFeatures';
/**
 * The single SLA control in the conversation header.
 *
 * The header used to hold two independent pieces: an "assign SLA" button that
 * hid itself once a policy existed, and a status chip that rendered only while
 * an SLA timer was actually pending - and was additionally hidden below the md
 * breakpoint. Between them they left two states with nothing on screen at all:
 * a narrow window, and a conversation whose SLA has no pending target left. In
 * both cases an agent could not tell whether the conversation was covered by an
 * SLA or not.
 *
 * This component owns all of those states in one always-present control:
 *   - no policy  -> the assign button (only while the feature is on)
 *   - policy + a pending target -> the live status chip
 *   - policy, no pending target -> a plain "covered by <policy>" chip
 */
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { evaluateSLAStatus } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import SLACardLabel from './SLACardLabel.vue';
import TeletelSlaAPI from 'dashboard/api/teletel/sla';
import {
  HEADER_ACTION_BUTTON_CLASS,
  HEADER_TEXT_CONTROL_CLASS,
} from 'dashboard/helper/conversationHeaderStyles';

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  parentWidth: {
    type: Number,
    default: 1000,
  },
});

const { isTeletelFeatureEnabled } = useTeletelFeatures();

const { t } = useI18n();
const store = useStore();

const isSlaFeatureEnabled = computed(() =>
  Boolean(isTeletelFeatureEnabled('sla'))
);

// Manual assignment is an sla_manage action on the server (owner order
// 2026-09-29); agents without the key only see the covered/uncovered state.
const currentRole = useMapGetter('getCurrentRole');
const currentUser = useMapGetter('getCurrentUser');
const accountId = useMapGetter('getCurrentAccountId');
const canManageSla = computed(
  () =>
    currentRole.value === 'administrator' ||
    getUserPermissions(currentUser.value, accountId.value).includes(
      'sla_manage'
    )
);

const hasPolicy = computed(() => Boolean(props.chat?.sla_policy_id));
const canAssign = computed(
  () => isSlaFeatureEnabled.value && canManageSla.value && !hasPolicy.value
);

// Same evaluation SLACardLabel does internally: it renders nothing without a
// threshold, so we need to know here whether it will draw anything.
const hasPendingTarget = computed(() =>
  Boolean(
    evaluateSLAStatus({
      appliedSla: props.chat?.applied_sla,
      chat: props.chat,
    })?.threshold
  )
);

const policyName = computed(() => {
  const policies = store.getters['sla/getSLA'] || [];
  const policy = policies.find(item => item.id === props.chat?.sla_policy_id);
  return policy?.name || '';
});

const coveredTooltip = computed(() =>
  policyName.value
    ? t('SLA.TELETEL.HEADER.COVERED_BY', { name: policyName.value })
    : t('SLA.TELETEL.HEADER.COVERED')
);

const slaPolicies = computed(() => store.getters['sla/getSLA']);
const isAssigning = ref(false);

const assignSla = async policyId => {
  isAssigning.value = true;
  try {
    await TeletelSlaAPI.assignConversation(props.chat.id, policyId);
    await store.dispatch('getConversation', props.chat.id);
    useAlert(t('SLA.TELETEL.ASSIGNMENT.SUCCESS'));
  } catch {
    useAlert(t('SLA.TELETEL.ASSIGNMENT.ERROR'));
  } finally {
    isAssigning.value = false;
  }
};
</script>

<template>
  <!-- policy with a live target: the status chip, at the row's height -->
  <SLACardLabel
    v-if="hasPolicy && hasPendingTarget"
    :chat="chat"
    show-extended-info
    :parent-width="parentWidth"
    :class="HEADER_TEXT_CONTROL_CLASS"
  />

  <!-- policy without a live target: still say the conversation is covered -->
  <span
    v-else-if="hasPolicy"
    v-tooltip.bottom="coveredTooltip"
    class="flex items-center gap-1.5 px-2"
    :class="[HEADER_TEXT_CONTROL_CLASS]"
  >
    <span class="i-lucide-shield-check size-4 text-n-teal-11" />
    <span
      v-if="policyName && parentWidth > 650"
      class="max-w-32 truncate text-xs font-medium text-n-slate-11"
    >
      {{ policyName }}
    </span>
  </span>

  <!-- no policy yet: the assign control -->
  <Popover v-else-if="canAssign" align="end">
    <template #default>
      <NextButton
        v-tooltip.bottom="t('SLA.TELETEL.ASSIGNMENT.ACTION')"
        ghost
        slate
        sm
        :class="HEADER_ACTION_BUTTON_CLASS"
        icon="i-lucide-timer-reset"
        :is-loading="isAssigning"
      />
    </template>
    <template #content>
      <div class="w-64 p-2">
        <p class="px-2 py-1 text-sm font-medium text-n-slate-12">
          {{ t('SLA.TELETEL.ASSIGNMENT.TITLE') }}
        </p>
        <button
          v-for="policy in slaPolicies"
          :key="policy.id"
          type="button"
          class="flex w-full rounded-lg px-2 py-2 text-start text-sm text-n-slate-12 hover:bg-n-alpha-2"
          @click="assignSla(policy.id)"
        >
          {{ policy.name }}
        </button>
        <p v-if="!slaPolicies.length" class="px-2 py-2 text-sm text-n-slate-11">
          {{ t('SLA.TELETEL.ASSIGNMENT.EMPTY') }}
        </p>
      </div>
    </template>
  </Popover>
</template>
